import { supportedLocales } from './supported-locales'
import { notifySubscribers } from './subscribers'
import { logError, logInfo, logWarn } from '@/utils/errorReporter'

export { localized } from './localized'

const CDN_BASE = 'https://cdn.ecoonline.com/i18n/suite-ui-kit'

const fallbacks: Record<string, Record<string, string>> = {}
const translations: Record<string, Record<string, string>> = {}
let activeLocale = 'en'
let rawLocale = 'en'

/**
 * Register a component group's bundled fallback translations.
 *
 * Called as a side-effect at module load time by each group's own
 * `src/<group>/i18n/index.ts`. After registration the group's translations
 * participate in every subsequent {@link setLocale} call.
 */
export function registerGroup(group: string, fallback: Record<string, string>): void {
  fallbacks[group] = fallback
  // Seed translations with the fallback so $t works before setLocale is called.
  if (!translations[group]) {
    translations[group] = { ...fallback }
  }
}

/**
 * Look up a localised string for a given group + key. Falls back to the
 * group's bundled `en.json`, then to the raw key.
 *
 * Group consumers should call this via their group-local wrapper rather than
 * passing the group name directly.
 */
export function $t(group: string, key: string): string {
  return translations[group]?.[key] ?? fallbacks[group]?.[key] ?? key
}

export function getLocale(): string {
  return activeLocale
}

/**
 * Returns the full normalised locale as originally provided to {@link setLocale}
 * (e.g. `'en-au'`), before resolution to a supported translation locale.
 *
 * Use this when looking up locale-keyed URLs from marketing data, where regional
 * variants exist even when translations don't differ (e.g. `en` vs `en-au`).
 */
export function getRawLocale(): string {
  return rawLocale
}

/**
 * Set the active locale for every registered component group.
 *
 * Fetches `<CDN_BASE>/<group>/<locale>.json` in parallel for each group.
 * Per-group failures fall back to the group's bundled `en.json` so a single
 * failing group does not block others. After all fetches settle, subscribers
 * are notified exactly once.
 *
 * If called before any group has registered, a warning is logged and the
 * call resolves without performing any fetches (still updating `activeLocale`
 * so subsequent registrations can use the value).
 */
export async function setLocale(locale: string): Promise<void> {
  if (!locale) return

  const normalized = locale.toLowerCase().replace(/_/g, '-')
  if (rawLocale === normalized) return
  rawLocale = normalized

  const base = normalized.split('-')[0]
  let resolved = 'en'

  // Use the full locale if supported, otherwise try the base language, otherwise fall back to 'en'
  if (supportedLocales.includes(normalized)) {
    resolved = normalized
  } else if (supportedLocales.includes(base)) {
    resolved = base
  }

  const groups = Object.keys(fallbacks)

  if (groups.length === 0) {
    logWarn('setLocale called before any component group registered', {
      source: 'setLocale',
      locale: resolved,
    })
    activeLocale = resolved
    notifySubscribers()
    return
  }

  await Promise.all(
    groups.map(async (group) => {
      try {
        const res = await fetch(`${CDN_BASE}/${group}/${resolved}.json`)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        translations[group] = await res.json()
        logInfo('Locale set', { source: 'setLocale', group, locale: resolved })
      } catch (error) {
        logError(error, { source: 'setLocale', group, locale: resolved })
        translations[group] = { ...fallbacks[group] }
      }
    }),
  )

  activeLocale = resolved
  notifySubscribers()
}

/**
 * Internal test helper. Clears all registered groups and resets the active
 * locale to `en`. Not part of the public API.
 *
 * @internal
 */
export function __resetForTests(): void {
  for (const k of Object.keys(fallbacks)) delete fallbacks[k]
  for (const k of Object.keys(translations)) delete translations[k]
  activeLocale = 'en'
  rawLocale = 'en'
}
